<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useEvents } from '@/composables/useEvents'
import { useToasts } from '@/composables/useToasts'
import { statusesByType } from '@/types'
import type { Entry, Update, Service, Component } from '@/types'
import MarkdownEditor from './MarkdownEditor.vue'
import DatetimeInput from '../DatetimeInput.vue'

const props = defineProps<{
  visible: boolean
  entry: Entry | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const { lang, t, getTypeLabel, getSeverityLabel, getStatusLabel, getEventTranslation } = useI18n()
const { servicesList, componentsList } = useEvents()
const { showToast } = useToasts()

// Form state
const formType = ref('incident')
const formSeverity = ref('minor')
const formStatus = ref('investigating')
const formService = ref('')
const formComponent = ref('')
const formStartAt = ref('')
const formEndAt = ref('')
const formTitleDe = ref('')
const formTitleEn = ref('')
const formSummaryDe = ref('')
const formSummaryEn = ref('')
const formDetailsDe = ref('')
const formDetailsEn = ref('')
const formUpdates = ref<Array<{
  at: string
  status: string
  messageDe: string
  messageEn: string
}>>([])

const activeTranslationTab = ref<'de' | 'en'>('de')
const submitting = ref(false)

// Available statuses based on current type
const availableStatuses = computed(() => {
  return statusesByType[formType.value] || []
})

// Components filtered by selected service
const filteredComponents = computed(() => {
  if (!formService.value) return []
  return componentsList.value.filter((c) => c.service === formService.value)
})

// Modal title
const modalTitle = computed(() => {
  return props.entry ? t('edit-event') : t('new-event')
})

// Watch type changes to update valid statuses
watch(formType, (type) => {
  const validStatuses = statusesByType[type] || []
  if (!validStatuses.includes(formStatus.value)) {
    formStatus.value = validStatuses[0] || ''
  }
})

// Watch service changes to reset component
watch(formService, () => {
  if (filteredComponents.value.length === 0 || !filteredComponents.value.find((c) => c.name === formComponent.value)) {
    formComponent.value = ''
  }
})

// Initialize form when entry changes
watch(
  () => props.visible,
  (visible) => {
    if (!visible) return
    if (props.entry) {
      populateFromEntry(props.entry)
    } else {
      resetForm()
    }
  }
)

function populateFromEntry(entry: Entry) {
  formType.value = entry.type
  formSeverity.value = entry.severity
  formStatus.value = entry.status
  formService.value = entry.service
  formComponent.value = entry.component || ''
  formStartAt.value = toDatetimeLocalValue(entry.start_at)
  formEndAt.value = entry.end_at ? toDatetimeLocalValue(entry.end_at) : ''
  formTitleDe.value = getEventTranslation(entry.title, 'de')
  formTitleEn.value = getEventTranslation(entry.title, 'en')
  formSummaryDe.value = getEventTranslation(entry.summary, 'de')
  formSummaryEn.value = getEventTranslation(entry.summary, 'en')
  formDetailsDe.value = getEventTranslation(entry.details, 'de')
  formDetailsEn.value = getEventTranslation(entry.details, 'en')
  formUpdates.value = (entry.updates || []).map((u) => ({
    at: toDatetimeLocalValue(u.at),
    status: u.status,
    messageDe: typeof u.message === 'string' ? u.message : getEventTranslation(u.message, 'de'),
    messageEn: typeof u.message === 'string' ? '' : getEventTranslation(u.message, 'en'),
  }))
  activeTranslationTab.value = 'de'
}

function resetForm() {
  formType.value = 'incident'
  formSeverity.value = 'minor'
  formStatus.value = 'investigating'
  formService.value = servicesList.value[0]?.name || ''
  formComponent.value = ''
  formStartAt.value = toDatetimeLocalValue(new Date().toISOString())
  formEndAt.value = ''
  formTitleDe.value = ''
  formTitleEn.value = ''
  formSummaryDe.value = ''
  formSummaryEn.value = ''
  formDetailsDe.value = ''
  formDetailsEn.value = ''
  formUpdates.value = []
  activeTranslationTab.value = 'de'
}

function toDatetimeLocalValue(isoString: string): string {
  if (!isoString) return ''
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromDatetimeLocalValue(value: string): string {
  if (!value) return ''
  return new Date(value).toISOString()
}

function addUpdate() {
  formUpdates.value.push({
    at: toDatetimeLocalValue(new Date().toISOString()),
    status: formStatus.value,
    messageDe: '',
    messageEn: '',
  })
}

function removeUpdate(index: number) {
  formUpdates.value.splice(index, 1)
  syncStatusFromUpdates()
}

function syncStatusFromUpdates() {
  if (formUpdates.value.length === 0) return
  // Find chronologically latest update
  let latest = formUpdates.value[0]
  for (const u of formUpdates.value) {
    if (u.at && u.at > latest.at) latest = u
  }
  if (latest.status) {
    formStatus.value = latest.status
  }
}

function getServiceLabel(service: Service | undefined): string {
  if (!service) return ''
  return service.translations?.[lang.value]?.name || service.name || ''
}

function getComponentLabelLocal(component: Component | undefined): string {
  if (!component) return ''
  return component.translations?.[lang.value]?.name || component.name || ''
}

function collectFormData() {
  if (!formStartAt.value || !formTitleDe.value || !formSummaryDe.value) {
    showToast('Please fill in all required fields.')
    return null
  }

  const data: any = {
    type: formType.value,
    severity: formSeverity.value,
    status: formStatus.value,
    service: formService.value,
    component: formComponent.value || undefined,
    start_at: fromDatetimeLocalValue(formStartAt.value),
    end_at: formEndAt.value ? fromDatetimeLocalValue(formEndAt.value) : undefined,
    title: { de: formTitleDe.value, en: formTitleEn.value },
    summary: { de: formSummaryDe.value, en: formSummaryEn.value },
    details: { de: formDetailsDe.value, en: formDetailsEn.value },
  }

  if (props.entry?.id) {
    data.id = props.entry.id
  }

  if (formUpdates.value.length > 0) {
    data.updates = formUpdates.value.map((u) => ({
      at: fromDatetimeLocalValue(u.at),
      status: u.status,
      message: { de: u.messageDe, en: u.messageEn },
    }))
  }

  return data
}

async function handleSubmit() {
  const formData = collectFormData()
  if (!formData) return

  submitting.value = true
  try {
    const isEdit = !!props.entry?.id
    const url = isEdit ? `/api/admin/events/${props.entry!.id}` : '/api/admin/events'
    const method = isEdit ? 'PUT' : 'POST'

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    })

    if (res.ok) {
      emit('saved')
      emit('close')
    } else {
      const data = await res.json().catch(() => ({}))
      showToast(data.message || 'Save failed.')
    }
  } catch (err) {
    showToast('Save failed.')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div v-if="visible" class="modal">
    <div class="modal-backdrop" @click="emit('close')"></div>
    <div class="modal-dialog">
      <div class="modal-header">
        <h3>{{ modalTitle }}</h3>
        <button type="button" class="icon-btn" aria-label="Close" @click="emit('close')">×</button>
      </div>
      <form class="modal-body" @submit.prevent="handleSubmit">
        <div class="form-grid">
          <label class="form-field">
            <span>{{ t('type-label') }}</span>
            <select v-model="formType" required>
              <option v-for="type in ['incident', 'maintenance', 'info']" :key="type" :value="type">
                {{ getTypeLabel(type) }}
              </option>
            </select>
          </label>
          <label class="form-field">
            <span>{{ t('severity-label') }}</span>
            <select v-model="formSeverity" required>
              <option v-for="sev in ['info', 'minor', 'major', 'critical']" :key="sev" :value="sev">
                {{ getSeverityLabel(sev) }}
              </option>
            </select>
          </label>
          <label class="form-field">
            <span>{{ t('status-label') }}</span>
            <select v-model="formStatus" required>
              <option v-for="status in availableStatuses" :key="status" :value="status">
                {{ getStatusLabel(status) }}
              </option>
            </select>
          </label>
          <label class="form-field">
            <span>{{ t('service-label-plain') }}</span>
            <select v-model="formService" required>
              <option v-for="svc in servicesList" :key="svc.name" :value="svc.name">
                {{ getServiceLabel(svc) }}
              </option>
            </select>
          </label>
          <label class="form-field">
            <span>{{ t('component-optional-label') }}</span>
            <select v-model="formComponent">
              <option value="">{{ t('none') }}</option>
              <option v-for="comp in filteredComponents" :key="comp.name" :value="comp.name">
                {{ getComponentLabelLocal(comp) }}
              </option>
            </select>
          </label>
          <label class="form-field">
            <span>{{ t('start-label') }}</span>
            <DatetimeInput v-model="formStartAt" required />
          </label>
          <label class="form-field">
            <span>{{ t('end-label') }}</span>
            <DatetimeInput v-model="formEndAt" />
          </label>
        </div>

        <!-- Translation tabs -->
        <div class="translation-tabs">
          <button type="button" :class="['translation-tab', { active: activeTranslationTab === 'de' }]" @click="activeTranslationTab = 'de'">Deutsch</button>
          <button type="button" :class="['translation-tab', { active: activeTranslationTab === 'en' }]" @click="activeTranslationTab = 'en'">English</button>
        </div>

        <!-- Title -->
        <div class="form-field">
          <span>{{ t('title-label') }}</span>
          <div v-show="activeTranslationTab === 'de'">
            <input v-model="formTitleDe" type="text" required>
          </div>
          <div v-show="activeTranslationTab === 'en'">
            <input v-model="formTitleEn" type="text">
          </div>
        </div>

        <!-- Summary -->
        <div class="form-field">
          <span>{{ t('summary-label') }}</span>
          <div v-show="activeTranslationTab === 'de'">
            <textarea v-model="formSummaryDe" rows="2" required></textarea>
          </div>
          <div v-show="activeTranslationTab === 'en'">
            <textarea v-model="formSummaryEn" rows="2"></textarea>
          </div>
        </div>

        <!-- Details (Markdown editor) -->
        <div class="form-field">
          <span>{{ t('details-optional-label') }}</span>
          <div v-show="activeTranslationTab === 'de'">
            <MarkdownEditor v-model="formDetailsDe" :visible="activeTranslationTab === 'de'" />
          </div>
          <div v-show="activeTranslationTab === 'en'">
            <MarkdownEditor v-model="formDetailsEn" :visible="activeTranslationTab === 'en'" />
          </div>
        </div>

        <!-- Updates section -->
        <div class="form-section">
          <div class="section-header">
            <span>{{ t('updates-label') }}</span>
            <button type="button" class="btn btn-ghost" @click="addUpdate">{{ t('add-update') }}</button>
          </div>
          <div class="updates-container">
            <div v-for="(update, idx) in formUpdates" :key="idx" class="update-row">
              <div class="update-row-header">
                <DatetimeInput v-model="update.at" required />
                <select v-model="update.status" :class="`status-${update.status}`" required @change="syncStatusFromUpdates">
                  <option v-for="s in availableStatuses" :key="s" :value="s">
                    {{ getStatusLabel(s) }}
                  </option>
                </select>
                <button type="button" class="btn btn-ghost btn-danger" @click="removeUpdate(idx)">×</button>
              </div>
              <div v-show="activeTranslationTab === 'de'" class="update-message-editor">
                <MarkdownEditor v-model="update.messageDe" :visible="activeTranslationTab === 'de'" />
              </div>
              <div v-show="activeTranslationTab === 'en'" class="update-message-editor">
                <MarkdownEditor v-model="update.messageEn" :visible="activeTranslationTab === 'en'" />
              </div>
            </div>
          </div>
        </div>
      </form>
      <div class="modal-footer">
        <button type="button" class="btn btn-ghost" @click="emit('close')">{{ t('cancel') }}</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          {{ submitting ? '...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>
